
import React, { useState, useMemo, useCallback } from 'react';

interface ChaosAnimationProps {
    count?: number;
    className?: string;
}

const ChaosAnimation: React.FC<ChaosAnimationProps> = ({ count = 100, className = "" }) => {
    const [form, setForm] = useState<'torus' | 'sphere'>('torus');
    
    const colors = ['#f72585', '#fee440', '#4cc9f0', '#e0e0e0'];

    const dots = useMemo(() => {
        const tempDots = [];
        // Adjust radius based on dot count to keep density looking good
        const maxRadius = count < 200 ? 160 : 240; 

        for (let i = 0; i < count; i++) {
            const size = Math.random() * 8 + 2;
            let x = 0, y = 0;

            if (form === 'torus') {
                const angle = Math.random() * 2 * Math.PI;
                const mainRadius = count < 200 ? 100 : 150;
                const tubeRadius = count < 200 ? 60 : 90;
                const tubeAngle = Math.random() * 2 * Math.PI;
                x = (mainRadius + tubeRadius * Math.cos(tubeAngle)) * Math.cos(angle);
                y = (mainRadius + tubeRadius * Math.cos(tubeAngle)) * Math.sin(angle);
            } else { // sphere
                const angle = Math.random() * 2 * Math.PI;
                // Use cube root of random to get a more uniform distribution within the sphere's volume
                const radius = Math.cbrt(Math.random()) * maxRadius; 
                x = radius * Math.cos(angle);
                y = radius * Math.sin(angle);
            }

            const scale = 0.5 + Math.random() * 0.5;

            tempDots.push({
                id: i,
                size,
                color: colors[Math.floor(Math.random() * colors.length)],
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
                animationDuration: `${Math.random() * 5 + 3}s`,
                animationDelay: `${Math.random() * -8}s`,
                transform: `scale(${scale})`,
            });
        }
        return tempDots;
    }, [form, count]);

    const toggleForm = useCallback(() => setForm(f => (f === 'torus' ? 'sphere' : 'torus')), []);


    return (
        <div 
            className={`relative w-full h-full min-h-[300px] lg:min-h-[500px] flex items-center justify-center cursor-pointer ${className}`}
            onClick={toggleForm}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleForm(); }}
            aria-label="Click to change animation shape"
        >
            <div 
                id="chaos-container" 
                className="relative w-full h-full"
            >
                {dots.map(dot => (
                    <div
                        key={dot.id}
                        className="dot-wrapper"
                        style={{
                            left: dot.left,
                            top: dot.top,
                            transform: dot.transform,
                            transition: 'left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), top 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        }}
                    >
                        <div
                            className="dot"
                            style={{
                                width: `${dot.size}px`,
                                height: `${dot.size}px`,
                                backgroundColor: dot.color,
                                animationDuration: dot.animationDuration,
                                animationDelay: dot.animationDelay,
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ChaosAnimation;
